
import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { ActionButton } from "../../components/Forms/ActionButton";
import { useGetBadgeList, useAssignBadgeToUser } from "../../queries/badge";
import { useGetUserByUserType } from "../../queries/user";
import NewDropdown from "../../components/Forms/NewDropdown";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from "react-router-dom";

const AssignBadge = () => {
    const navigate = useNavigate();
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedBadge, setSelectedBadge] = useState(null);

    const { data: badgeList, isLoading: isBadgeListLoading } = useGetBadgeList();
    const { data: userList, isLoading: isUserListLoading } = useGetUserByUserType({
        toast,
        userType: "user"
    });

    const { mutate: assignBadge, isPending: isAssigning } = useAssignBadgeToUser();

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: {
            userId: null,
            badgeId: null,
        }
    });

    const onSubmit = (data) => {
        if (!data.userId) {
            toast.error("Please select a user.");
            return;
        }
        if (!data.badgeId) {
            toast.error("Please select a badge.");
            return;
        }

        const loadingToastId = toast.loading('Assigning badge...');
        
        assignBadge(
            {
                userId: data.userId,
                badgeId: data.badgeId,
            },
            {
                onSuccess: (res) => {
                    toast.dismiss(loadingToastId);
                    if (res?.error) {
                        toast.error("Error assigning badge. Please try again.", {
                            autoClose: 5000,
                        });
                        return;
                    }
                    
                    toast.success(res.message || "Badge assigned successfully!", {
                        autoClose: 3000,
                        onClose: () => {
                            navigate('/badge/badge-main/view-badges');
                        }
                    });
                },
                onError: (error) => {
                    toast.dismiss(loadingToastId);
                    console.error("Error assigning badge:", error);
                    toast.error("Error assigning badge. Please try again.", {
                        autoClose: 5000,
                    });
                }
            }
        );
    };

    const handleUserChange = (selectedOption) => {
        setSelectedUser(selectedOption.value);
    };

    const handleBadgeChange = (selectedOption) => {
        setSelectedBadge(selectedOption.value);
    };

    if (isBadgeListLoading || isUserListLoading) {
        return <div className="flex justify-center items-center h-screen">
            <p className="text-lg font-semibold">Loading...</p>
        </div>
    }

    // Transform badge data for dropdown
    const badgeOptions = Array.isArray(badgeList) ? badgeList.map(badge => ({
        _id: badge._id,
        name: badge.name,
    })) : [];

    // Transform user data for dropdown
    const userOptions = Array.isArray(userList?.users) ? userList.users.map(user => ({
        _id: user._id,
        name: `${user.username} (${user.email})`,
    })) : [];

    return (
        <form className="flex flex-col w-full font-semibold" onSubmit={handleSubmit(onSubmit)}>
            <section className="flex flex-col justify-center px-5 pt-2 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] relative overflow-visible">
                <div className="flex items-center justify-between">
                    <p className="text-base font-bold mt-3">Assign Badge to User</p>
                </div>

                <div className="flex flex-col gap-4">
                    {/* User Selection */}
                    <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5 relative z-10">
                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                            Select User
                        </label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <Controller
                                name="userId"
                                control={control}
                                rules={{ required: "Please select a user" }}
                                render={({ field: { onChange, value } }) => (
                                    <NewDropdown
                                        placeholder="Select a user"
                                        dataList={userOptions}
                                        value={value}
                                        onChange={(selected) => {
                                            onChange(selected.value);
                                            handleUserChange(selected);
                                        }}
                                        allowMultiple={false}
                                    />
                                )}
                            />
                        </div>
                        {errors.userId && <p className="text-red text-sm">{errors.userId.message}</p>}
                    </div>

                    {/* Badge Selection */}
                    <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5 relative z-20">
                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                            Select Badge
                        </label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <Controller
                                name="badgeId"
                                control={control}
                                rules={{
                                    required: "Please select a badge"
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <NewDropdown
                                        placeholder="Select a badge"
                                        dataList={badgeOptions}
                                        value={value}
                                        onChange={(selected) => {
                                            onChange(selected.value);
                                            handleBadgeChange(selected);
                                        }}
                                        allowMultiple={false}
                                    />
                                )}
                            />
                        </div>
                        {errors.badgeId && <p className="text-red text-sm">{errors.badgeId.message}</p>}
                    </div>
                </div>

                <div className="mt-10 flex gap-5 items-center justify-between">
                    <ActionButton
                        variant="reset"
                        text="Reset"
                        handleSubmit={() => {
                            reset({
                                userId: null,
                                badgeId: null,
                            });
                            setSelectedUser(null);
                            setSelectedBadge(null);
                        }}
                    />
                    <div className="flex items-center gap-3">
                        <ActionButton
                            variant="cancel"
                            text="Cancel"
                            handleSubmit={() => navigate('/badge/badge-main/view-badges')}
                        />
                        <ActionButton
                            variant="submit"
                            text="Assign Badge"
                            handleSubmit={() => { }}
                            disabled={isAssigning}
                        />
                    </div>
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick={true}
                rtl={false}
                pauseOnFocusLoss={true}
                draggable={true}
                pauseOnHover={true}
                theme="light"
                style={{ zIndex: 9999 }}
            />
        </form>
    );
}

export default AssignBadge;